import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, ClipboardList, Receipt, Package, ShieldCheck, Users, Sparkles, Lock, DatabaseBackup } from 'lucide-react';
import { DemoContext, DemoButton } from './ui';
import { DemoDialog } from './DemoDialog';
import { SEGMENTS } from './content';
import { FEATURES } from './features';
import { MERCHANT } from '../legal';
import './site.css';

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,600;12..96,700&family=Manrope:wght@400;500;600;700&display=swap';

const ICONS = { ClipboardList, Receipt, Package, ShieldCheck, Users, Sparkles };
const MENUS = {
  solutions: { label: 'Solutions', items: SEGMENTS.map((s) => ({ key: s.slug, to: `/driveline/solutions/${s.slug}`, label: s.tile, image: s.image })) },
  features: { label: 'Features', items: FEATURES.map((f) => ({ key: f.slug, to: `/driveline/features/${f.slug}`, label: f.name, Icon: ICONS[f.icon] })) },
};
const navCls = 'text-[15px] font-medium text-[#334155] hover:text-[#0f172a]';

const DropMenu = ({ id, onPick, mobile }) => {
  const [open, setOpen] = useState(false);
  const p = mobile ? 'mobile-' : '';
  const { label, items } = MENUS[id];
  return (
    <div className={mobile ? 'w-full' : 'relative'} onMouseEnter={() => !mobile && setOpen(true)} onMouseLeave={() => !mobile && setOpen(false)}>
      <button type="button" onClick={() => (mobile ? setOpen((o) => !o) : setOpen(true))} className={`${navCls} inline-flex items-center gap-1`} data-testid={`${p}nav-${id}`}>{label} <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} /></button>
      {open && (
        <div className={`${mobile ? 'relative mt-2 w-80 max-w-full' : 'absolute left-0 top-full z-50 w-80'} rounded-2xl border border-[#e2e0d8] bg-[#ffffff] p-2 shadow-xl`} data-testid={`${p}nav-${id}-menu`}>
          {items.map(({ key, to, label: text, image, Icon }) => (
            <Link key={key} to={to} onClick={() => { setOpen(false); onPick?.(); }} className="flex items-center gap-3 rounded-xl p-2 hover:bg-[#f1efe9]" data-testid={`${p}nav-${id === 'solutions' ? 'solution' : 'feature'}-${key}`}>
              {image ? <img src={image} alt="" className="h-10 w-14 rounded-lg object-cover" /> : <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#ccfbf1] text-[#0f766e]"><Icon size={18} /></span>}
              <span className="text-sm font-semibold text-[#0f172a]">{text}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const NavLinks = ({ onPick, mobile }) => {
  const p = mobile ? 'mobile-' : '';
  return (
    <>
      <DropMenu id="solutions" mobile={mobile} onPick={onPick} />
      <DropMenu id="features" mobile={mobile} onPick={onPick} />
      <Link to="/driveline/compare" onClick={onPick} className={navCls} data-testid={`${p}nav-compare`}>Why switch</Link>
      <Link to="/driveline/switch" onClick={onPick} className={navCls} data-testid={`${p}nav-switch`}>Switch to us</Link>
      <Link to="/driveline/stories" onClick={onPick} className={navCls} data-testid={`${p}nav-stories`}>Stories</Link>
      <Link to="/driveline/pricing" onClick={onPick} className={navCls} data-testid={`${p}nav-pricing`}>Pricing</Link>
      <Link to="/driveline/guides" onClick={onPick} className={navCls} data-testid={`${p}nav-guides`}>Guides</Link>
      <Link to="/login" onClick={onPick} className={navCls} data-testid={`${p}nav-signin`}>Sign in</Link>
    </>
  );
};

const Nav = () => {
  const [menu, setMenu] = useState(false);
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-[#e7e5df] bg-[rgba(247,245,240,.88)] backdrop-blur-lg" data-testid="driveline-nav">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-8 px-5 sm:px-8">
        <Link to="/driveline" data-testid="nav-logo"><img src="/brand/driveline-logo.png" alt="Driveline" className="h-7 w-auto" /></Link>
        <nav className="hidden items-center gap-7 lg:flex"><NavLinks /></nav>
        <div className="ml-auto hidden lg:block"><DemoButton testid="nav-book-demo" className="!px-5 !py-2.5 !text-sm" /></div>
        <button type="button" className="ml-auto rounded-lg p-2 lg:hidden" onClick={() => setMenu((m) => !m)} aria-label="Menu" data-testid="nav-mobile-toggle">{menu ? <X size={22} /> : <Menu size={22} />}</button>
      </div>
      {menu && <nav className="flex flex-col items-start gap-4 border-t bg-[#f7f5f0] border-[#e7e5df] px-5 py-5 lg:hidden" data-testid="nav-mobile-menu"><NavLinks mobile onPick={() => setMenu(false)} /></nav>}
    </header>
  );
};

const Footer = () => (
  <footer className="border-t border-[#e7e5df] bg-[#f1efe9] pb-28 pt-14 lg:pb-14" data-testid="driveline-footer">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 md:grid-cols-5">
      <div className="md:col-span-2">
        <img src="/brand/driveline-logo.png" alt="Driveline" className="h-7 w-auto" />
        <p className="mt-4 max-w-sm text-sm text-[#475569]">Workshop software built and used on our own floor at {MERCHANT.legal}, {MERCHANT.address}.</p>
        <p className="mt-3 text-sm text-[#475569]">{MERCHANT.email} · {MERCHANT.phone}</p>
      </div>
      <div className="space-y-2 text-sm">
        <p className="font-semibold text-[#0f172a]">Solutions</p>
        {SEGMENTS.map((s) => <Link key={s.slug} to={`/driveline/solutions/${s.slug}`} className="block text-[#475569] hover:text-[#0f172a]">{s.tile}</Link>)}
      </div>
      <div className="space-y-2 text-sm" data-testid="footer-features">
        <p className="font-semibold text-[#0f172a]">Features</p>
        {FEATURES.map((f) => <Link key={f.slug} to={`/driveline/features/${f.slug}`} className="block text-[#475569] hover:text-[#0f172a]" data-testid={`footer-feature-${f.slug}`}>{f.name}</Link>)}
      </div>
      <div className="space-y-2 text-sm">
        <p className="font-semibold text-[#0f172a]">Driveline</p>
        {[['Switching from another software?', '/driveline/switch'], ['Driveline vs paper & Excel', '/driveline/compare'], ['Workshop stories', '/driveline/stories'], ['Pricing', '/driveline/pricing'], ['Guides', '/driveline/guides'], ['Terms', '/driveline/terms'], ['Privacy', '/driveline/privacy'], ['Refunds', '/driveline/refund-policy']].map(([l, to]) => <Link key={to} to={to} className="block text-[#475569] hover:text-[#0f172a]">{l}</Link>)}
      </div>
    </div>
    <Link to="/driveline/privacy" data-testid="footer-trust-strip" className="mx-auto mt-10 flex max-w-7xl flex-wrap items-center justify-center gap-x-5 gap-y-2 border-t border-[#e2e0d8] px-5 pt-6 text-xs font-medium text-[#475569] transition-colors hover:text-[#0f766e] sm:px-8">
      {[[ShieldCheck, 'DPDP & GDPR'], [Lock, 'Encrypted'], [DatabaseBackup, 'Backed up']].map(([Icon, t]) => (
        <span key={t} className="inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5 text-[#0f766e]" />{t}</span>
      ))}
    </Link>
  </footer>
);

export const SiteShell = ({ children }) => {
  const [demo, setDemo] = useState(false);
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!document.querySelector(`link[href="${FONT_HREF}"]`)) {
      const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = FONT_HREF; document.head.appendChild(l);
    }
  }, []);
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    // Images above the target settle late, so aim again once layout has grown.
    const go = () => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
    const timers = [150, 700, 1500].map((ms) => setTimeout(go, ms));
    return () => timers.forEach(clearTimeout);
  }, [pathname, hash]);
  return (
    <DemoContext.Provider value={{ open: () => setDemo(true) }}>
      <div className="dl-site min-h-screen">
        <Nav />
        <main>{children}</main>
        <Footer />
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#e7e5df] bg-[rgba(247,245,240,.95)] p-3 backdrop-blur lg:hidden">
          <DemoButton testid="mobile-book-demo" className="w-full" />
        </div>
        {demo && <DemoDialog onClose={() => setDemo(false)} />}
      </div>
    </DemoContext.Provider>
  );
};

export default SiteShell;
